import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import { sortBy } from 'es-toolkit'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box } from '../../../../components/Box.tsx'
import { CustomRadioGroup } from '../../../../components/CustomRadioGroup.tsx'
import { I18n } from '../../../../components/I18n.tsx'
import { Icon } from '../../../../components/Icon.tsx'
import { SearchField } from '../../../../components/SearchField.tsx'
import { ids } from '../../../../ids.ts'
import { useSearchLocations } from '../../../locations/locationUtils.ts'
import type { ReportFormProps } from '../CreateReportForm.tsx'
import type { BaseReportForm } from '../schema.ts'
import { useSelectLocations } from '../useSelectLocations.ts'
import { LOCATION_ROW_HEIGHT, MultipleLocationPicker } from './MultipleLocationPicker.tsx'

const MIN_LOCATIONS_FOR_SEARCH = 5

export const LocationList = ({
	locations,
	selectedLocations,
	onLocationsSelected,
	allowUnprotectedAssets,
	maxAssets,
}: ReportFormProps & { allowUnprotectedAssets: boolean; maxAssets: number }) => {
	const selectLocations = useSelectLocations(onLocationsSelected)
	const [search, setSearch] = useState('')
	const searchedLocations = useSearchLocations(locations, search)
	const locationOptions = sortBy(
		searchedLocations.map((location) => ({ label: location.name, icon: location.icon, value: location.id })),
		['label'],
	)

	const { control } = useFormContext<BaseReportForm>()
	return (
		<>
			{locations.length === 0 ? (
				<Alert icon={<Icon name='CircleInfoSolid' />} severity='warning'>
					<I18n i18nKey={($) => $.reports.form.noAssets} />
				</Alert>
			) : maxAssets > 1 ? (
				<MultipleLocationPicker locations={locations} selectedLocations={selectedLocations} onLocationsSelected={onLocationsSelected} />
			) : (
				<>
					{locations.length >= MIN_LOCATIONS_FOR_SEARCH && (
						<SearchField
							data-ta={ids.reports.form.areaOfInterest.search}
							clearDataTa={ids.reports.form.areaOfInterest.searchXBtn}
							autoFocus
							value={search}
							onChange={setSearch}
						/>
					)}
					<Box
						overflow='auto'
						minHeight={Math.min(4 * LOCATION_ROW_HEIGHT, locationOptions.length * LOCATION_ROW_HEIGHT)}
						maxHeight={6 * LOCATION_ROW_HEIGHT}
					>
						<CustomRadioGroup
							options={locationOptions}
							value={selectedLocations[0]?.id}
							onChange={(selectedLocationId) => {
								const location = locations.find((loc) => loc.id === selectedLocationId)
								selectLocations(location ? [location] : [])
							}}
							data-ta={ids.reports.form.areaOfInterest.location}
						/>
					</Box>
				</>
			)}
			<Controller
				control={control}
				name='assetVrns'
				render={({ fieldState }) => (fieldState.error ? <Typography color='error'>{fieldState.error.message}</Typography> : <></>)}
			/>
			{!allowUnprotectedAssets && (
				<Alert icon={<Icon name='CircleInfoSolid' />} severity='info'>
					<I18n i18nKey={($) => $.reports.form.visibleAssetsDisclaimer} />
				</Alert>
			)}
		</>
	)
}
